// Helpers for generating schema.org JSON-LD structured data.
// Centralized so Article + BreadcrumbList stay consistent across pages.
import { SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE, canonical } from './seo';

export interface BreadcrumbCrumb {
  name: string;
  path: string;
}

/** BreadcrumbList schema — pass crumbs in order from root to current page. */
export const breadcrumbListSchema = (crumbs: BreadcrumbCrumb[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: crumbs.map((crumb, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: crumb.name,
    item: canonical(crumb.path),
  })),
});

export interface ArticleSchemaInput {
  type?: 'BlogPosting' | 'Article' | 'Report';
  title: string;
  description: string;
  slug: string;
  pathPrefix: '/blog' | '/report' | '/case-study';
  coverImage: string;
  datePublished: string;
  dateModified?: string;
  author: { name: string; title?: string; avatar?: string };
  categories?: string[];
  keywords?: string[];
  readTimeMinutes?: number;
  wordCount?: number;
}

/** Article / BlogPosting / Report schema with Fordje publisher block. */
export const articleSchema = (input: ArticleSchemaInput) => {
  const url = `${SITE_URL}${input.pathPrefix}/${input.slug}`;
  return {
    '@context': 'https://schema.org',
    '@type': input.type ?? 'BlogPosting',
    headline: input.title,
    description: input.description,
    image: [input.coverImage, DEFAULT_OG_IMAGE],
    datePublished: input.datePublished,
    dateModified: input.dateModified ?? input.datePublished,
    author: {
      '@type': 'Person',
      name: input.author.name,
      ...(input.author.title ? { jobTitle: input.author.title } : {}),
      ...(input.author.avatar ? { image: input.author.avatar } : {}),
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: DEFAULT_OG_IMAGE,
        width: 512,
        height: 512,
      },
      url: SITE_URL,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
    ...(input.keywords?.length || input.categories?.length
      ? {
          keywords: [...(input.keywords ?? []), ...(input.categories ?? [])].join(', '),
        }
      : {}),
    ...(input.readTimeMinutes ? { timeRequired: `PT${input.readTimeMinutes}M` } : {}),
    ...(input.wordCount ? { wordCount: input.wordCount } : {}),
    inLanguage: 'en-US',
    isAccessibleForFree: true,
    ...(input.categories?.length
      ? {
          about: input.categories.map((c) => ({ '@type': 'Thing', name: c })),
        }
      : {}),
  };
};
